import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react'
import { api, getToken, getStoredUser, clearTokens, setOnUnauthorized } from './api'
import LoginPage from './components/LoginPage'
import CodeMirror from '@uiw/react-codemirror'
import { sql as sqlLang } from '@codemirror/lang-sql'
import { oneDark } from '@codemirror/theme-one-dark'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import Sidebar from './components/Sidebar'
import MetaSettings from './components/MetaSettings'
import SystemSettings from './components/SystemSettings'
import InstructionEditor from './components/InstructionEditor'
import ProfileModal from './components/ProfileModal'
import GenerateBar from './components/GenerateBar'
import YamlEditor from './components/YamlEditor'
import OutputTabs from './components/OutputTabs'
import CompositeViewer from './components/CompositeViewer'
import SqlConverter from './components/SqlConverter'
import SqlConverterWorkbench from './components/SqlConverterWorkbench'
import Explore from './components/Explore'
import VirtualSchemaViewer from './components/VirtualSchemaViewer'
import GcsExportDialog from './components/common/GcsExportDialog'
import TokenBadge from './components/common/TokenBadge'
import { downloadText, ensureExtension } from './utils/download'
import { suggestDuplicateName } from './utils/duplicateName'
import { copyToClipboard } from './utils/clipboard'
import DuplicateDialog from './components/common/DuplicateDialog'
import SvRecommendDialog from './components/common/SvRecommendDialog'
// Sandbox 는 react-markdown + prism-react-renderer 등 무거운 의존성을 포함해
// 초기 앱 로드를 지연시키므로 lazy 로 분리. 첫 클릭 시 chunk 다운로드 후 캐시.
const SandboxChat = lazy(() => import('./components/SandboxChat/SandboxChat'))

function SandboxFallback() {
  return (
    <div className="sb-root">
      <div className="sb-sessions">
        <div style={{ padding: 14, color: 'var(--text-dim)', fontSize: 13 }}>로드 중…</div>
      </div>
      <div className="sb-chat">
        <div className="sb-chat-empty">Sandbox 로드 중…</div>
      </div>
    </div>
  )
}
import { APP_DEFAULTS } from './defaults'

function formatElapsed(sec) {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m > 0 ? `${m}분 ${s}초` : `${s}초`
}

// 비활성화된 출력 탭 (재활성화 시 해당 key를 제거)
const DISABLED_TABS = new Set(['ddl', 'lookml', 'yaml', 'bq_mv', 'pub_ctx'])

// KBNL-59: 탭 보존용 — appMode 와 1:1 매핑되는 hash 화이트리스트
const APP_MODE_HASHES = new Set(['explore', 'metric', 'semantic', 'sql', 'sql_wb', 'vschema', 'sandbox'])
function readHashMode() {
  if (typeof window === 'undefined') return null
  const h = window.location.hash.replace(/^#/, '')
  return APP_MODE_HASHES.has(h) ? h : null
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    if (!value) return
    // HTTP(비-secure context) 폴백 포함 공용 유틸 — 고객사 내부망 http 접속 대응
    copyToClipboard(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }
  return (
    <button className="btn-copy" onClick={handleCopy}>
      {copied ? '✓ 복사됨' : '복사'}
    </button>
  )
}

// 프론트엔드 fallback 기본값 (서버 /api/defaults 응답으로 덮어씌워짐)
// 저장소/환경 기본값은 빌드 타임에 run.env 로부터 주입된다 (./defaults.js → APP_DEFAULTS)
const FALLBACK_SETTINGS = {
  project_id:      APP_DEFAULTS.project_id,
  location:        APP_DEFAULTS.location,
  dataset_id:      '',
  table_ids:       [],
  selected_tables: {},
  glossary_id:     '',
  glossary_ids:    [],
  vertex_location: APP_DEFAULTS.location,
  model_name:      APP_DEFAULTS.model_name,
  catalog:         'my_catalog',
  schema_name:     'my_schema',
  storage_type:    APP_DEFAULTS.storage_type,
  gcs_path:        APP_DEFAULTS.gcs_path,
  fs_project:      APP_DEFAULTS.fs_project,
  fs_database:     APP_DEFAULTS.fs_database,
  fs_collection:   APP_DEFAULTS.fs_collection,
  user_id:         '',
  font_size:       13,
  theme:           'light',
}

export default function App() {
  // ── 인증 ──────────────────────────────────────────────────────────────────
  const [authEnabled, setAuthEnabled] = useState(null)
  const [authUser, setAuthUser] = useState(() => getStoredUser())
  const [authChecked, setAuthChecked] = useState(false)
  // KBNL-57: Virtual Schema 기능 활성화 여부 — 환경변수 VIRTUAL_SCHEMA_ENABLED.
  // localStorage 캐시와 무관하게 매번 /api/defaults 에서 받음 (운영자가 운영 중
  // 토글하면 다음 페이지 로드 때 즉시 반영). null = 아직 미확인 (로딩).
  const [virtualSchemaEnabled, setVirtualSchemaEnabled] = useState(null)
  // KBNL-93 후속: Explore 메뉴 노출 여부 — env EXPLORE_MENU_ENABLED 기본 true.
  // false 로 명시 토글하면 헤더 메뉴에서 숨김. URL hash '#explore' 직접 접근은 그대로
  // 동작 (코드/라우팅 보존).
  const [exploreMenuEnabled, setExploreMenuEnabled] = useState(true)
  // KBNL-99 §2: 통합 Workbench 가 기본 진입점. 기존 SQL Converter / Semantic View 는
  // LEGACY_MENUS_ENABLED env 로 숨김 (기본 false). 코드/라우팅 보존 — URL hash
  // '#sql' / '#metric' 직접 접근은 그대로 동작.
  const [legacyMenusEnabled, setLegacyMenusEnabled] = useState(false)

  useEffect(() => {
    api.getDefaults()
      .then(d => {
        setAuthEnabled(d.auth_enabled === true)
        // 명시적 false 만 OFF, 그 외 (true/누락/legacy 응답) 모두 ON — backward-compat
        setVirtualSchemaEnabled(d.virtual_schema_enabled !== false)
        // Explore 는 기본 노출 — 명시적 false 일 때만 숨김.
        setExploreMenuEnabled(d.explore_menu_enabled !== false)
        // KBNL-99 §2: 기존 메뉴는 기본 숨김 — 명시적 true 일 때만 노출.
        setLegacyMenusEnabled(d.legacy_menus_enabled === true)
      })
      .catch(() => {
        setAuthEnabled(false)
        setVirtualSchemaEnabled(true)  // 네트워크 실패 시 기존 동작 유지
      })
  }, [])

  useEffect(() => {
    if (authEnabled === null) return
    if (!authEnabled) {
      // 인증 비활성화 모드: 서버가 기본 사용자(user/사용자)를 내려준다
      api.getMe()
        .then(u => { setAuthUser(u); setAuthChecked(true) })
        .catch(() => { setAuthUser(null); setAuthChecked(true) })
      return
    }
    const tok = getToken()
    if (!tok) { setAuthUser(null); setAuthChecked(true); return }
    api.getMe()
      .then(u => { setAuthUser(u); setAuthChecked(true) })
      .catch(() => { clearTokens(); setAuthUser(null); setAuthChecked(true) })
  }, [authEnabled])

  useEffect(() => { setOnUnauthorized(() => { clearTokens(); setAuthUser(null) }) }, [])

  const handleLogin  = useCallback((user) => setAuthUser(user), [])
  const handleLogout = useCallback(() => { clearTokens(); setAuthUser(null) }, [])

  // ── 설정 ──────────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('svg_settings')
      return saved ? { ...FALLBACK_SETTINGS, ...JSON.parse(saved) } : FALLBACK_SETTINGS
    } catch { return FALLBACK_SETTINGS }
  })
  const [settingsPage, setSettingsPage] = useState(null)  // null | 'meta' | 'system'
  const [showProfile, setShowProfile] = useState(false)
  // GCS Export 다이얼로그 (Semantic View 메인 모드 — KBNL-27)
  const [showSemanticExport, setShowSemanticExport] = useState(false)

  // 서버 환경변수 기본값 로드 (최초 1회, localStorage에 저장된 설정이 없을 때)
  useEffect(() => {
    const hasSaved = !!localStorage.getItem('svg_settings')
    if (hasSaved) return
    api.getDefaults().then(defaults => {
      const merged = { ...FALLBACK_SETTINGS, ...defaults }
      setSettings(merged)
      localStorage.setItem('svg_settings', JSON.stringify(merged))
    }).catch(() => {})
  }, [])

  // 사용자별 설정 로드 (로그인 직후, 서버에 저장된 설정이 있으면 적용)
  useEffect(() => {
    if (!authChecked || !authUser) return
    api.getUserSettings()
      .then(({ settings: userSettings, exists }) => {
        if (!exists || !userSettings || Object.keys(userSettings).length === 0) return
        setSettings(prev => {
          const merged = { ...prev, ...userSettings }
          localStorage.setItem('svg_settings', JSON.stringify(merged))
          return merged
        })
      })
      .catch(() => {})
  }, [authChecked, authUser])

  // 스토리지 스코프 user_id 를 로그인 사용자와 항상 일치시킨다.
  // (이전 사용자의 settings 가 localStorage 에 남아있으면, 새로 로그인한 사용자가
  //  의도치 않게 이전 사용자 스코프에 저장하게 되는 함정을 차단)
  useEffect(() => {
    if (!authChecked || !authUser?.user_id) return
    setSettings(prev => {
      if (prev.user_id === authUser.user_id) return prev
      const merged = { ...prev, user_id: authUser.user_id }
      localStorage.setItem('svg_settings', JSON.stringify(merged))
      return merged
    })
  }, [authChecked, authUser])

  // ── 현재 뷰 상태 ───────────────────────────────────────────────────────────
  const [viewName,      setViewName]      = useState('')
  const [description,   setDescription]   = useState('')
  const [prompt,        setPrompt]        = useState('')
  const [semanticYaml,       setSemanticYaml]       = useState('')
  const [semanticYamlOrig,   setSemanticYamlOrig]   = useState('')
  const [semanticYamlDirty,  setSemanticYamlDirty]  = useState(false)
  const [sqlText,            setSqlText]            = useState('')  // SQL 원본
  const [sqlConverting,      setSqlConverting]      = useState(false)
  const [sqlElapsed,         setSqlElapsed]         = useState(0)
  const sqlTimerRef = useRef(null)
  // const sqlSpliterRef = useRef(null)
  const [yamlContent,   setYamlContent]   = useState('')
  const [yamlOriginal,  setYamlOriginal]  = useState('')  // 생성/로드 시점의 원본
  const [ddl,           setDdl]           = useState('')
  const [ddlOriginal,   setDdlOriginal]   = useState('')
  const [bqView,        setBqView]        = useState('')
  const [bqViewOriginal,setBqViewOriginal]= useState('')
  const [bqMv,          setBqMv]          = useState('')
  const [bqMvOriginal,  setBqMvOriginal]  = useState('')
  const [bqMvAnalysis,  setBqMvAnalysis]  = useState(null)
  const [lookml,        setLookml]        = useState('')
  const [lookmlOriginal,setLookmlOriginal]= useState('')
  const [pubCtx,        setPubCtx]        = useState('')
  const [pubCtxOriginal,setPubCtxOriginal]= useState('')
  const [validation,    setValidation]    = useState(null)
  const [yamlDirty,     setYamlDirty]     = useState(false)
  const [ddlDirty,      setDdlDirty]      = useState(false)
  const [bqViewDirty,   setBqViewDirty]   = useState(false)
  const [bqMvDirty,     setBqMvDirty]     = useState(false)
  const [lookmlDirty,   setLookmlDirty]   = useState(false)
  const [pubCtxDirty,   setPubCtxDirty]   = useState(false)
  const [isSaved,       setIsSaved]       = useState(false)  // 한 번이라도 저장된 적 있는지

  // ── UI 상태 ────────────────────────────────────────────────────────────────
  const [activeTab,     setActiveTab]     = useState('semantic')
  const [savedViews,    setSavedViews]    = useState([])
  const [selectedView,  setSelectedView]  = useState(null)
  // KBNL-141(리뷰): 협업 모드 동명이인 충돌 방지 — 선택/리네임을 owner 까지 구분.
  const [selectedViewOwner, setSelectedViewOwner] = useState('')
  // KBNL-141(리뷰): 본인 항목 여부 — 타인 SV 선택 시 삭제 차단용.
  const [selectedViewIsMine, setSelectedViewIsMine] = useState(true)
  // KBNL-69: 본문 헤더 토큰 배지용 — 마지막 저장된 SV doc 의 token_count
  const [selectedViewTokenCount, setSelectedViewTokenCount] = useState(null)
  // KBNL-74: SV 탭에서 "유사 SV 찾기" 모달 (선택된 SV 기반 → 결과 클릭 시 해당 SV 로 이동)
  const [recommendOpen, setRecommendOpen] = useState(false)
  const [isGenerating,  setIsGenerating]  = useState(false)
  const [isConverting,  setIsConverting]  = useState(false)
  const [isSaving,      setIsSaving]      = useState(false)
  const [toast,         setToast]         = useState(null)  // {type:'success'|'error', msg}
  const [elapsed,       setElapsed]       = useState(0)     // 생성 경과 시간(초)
  const abortRef = useRef(null)   // AbortController
  const timerRef = useRef(null)   // setInterval ID
  const startTimeRef = useRef(0)  // 시작 시각
  // 'explore' | 'metric' | 'semantic' | 'sql' | 'vschema' | 'sandbox'
  // KBNL-59: URL hash 와 양방향 sync 해서 F5 / 즐겨찾기 / 브라우저 뒤로앞으로 보존
  const [appMode, setAppMode] = useState(() => readHashMode() || 'explore')

  // appMode 가 바뀌면 location.hash 갱신 — 동일하면 no-op (hashchange 무한루프 방지).
  // replaceState 로 history 엔트리를 쌓지 않아 새로고침 시에만 보존되고 뒤로가기는
  // 진짜 페이지 단위(로그인 등) 로만 동작.
  useEffect(() => {
    const cur = window.location.hash.replace(/^#/, '')
    if (cur !== appMode) {
      window.history.replaceState(null, '', `#${appMode}`)
    }
  }, [appMode])

  // 브라우저 뒤로/앞으로 → hashchange → state 동기화
  useEffect(() => {
    const onHashChange = () => {
      const m = readHashMode()
      if (m && m !== appMode) setAppMode(m)
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [appMode])

  // KBNL-57: virtual schema 가 disabled 인데 mode 가 'vschema' 면 explore 로 fallback
  // (운영자가 ENV 토글 후 사용자가 옛 탭에 머물러 있는 케이스)
  useEffect(() => {
    if (virtualSchemaEnabled === false && appMode === 'vschema') {
      setAppMode('explore')
    }
  }, [virtualSchemaEnabled, appMode])

  // 탭 전환 — settingsPage 가 열려 있으면 함께 닫는다 (탭 가려짐 버그 방지)
  const switchMode = useCallback((m) => {
    setAppMode(m)
    setSettingsPage(null)
  }, [])

  // ── 토스트 ─────────────────────────────────────────────────────────────────
  const toastTimerRef = useRef(null)
  const showToast = useCallback((type, msg) => {
    if (toastTimerRef.current) { clearTimeout(toastTimerRef.current); toastTimerRef.current = null }
    setToast({ type, msg })
    // error는 수동 닫기, success는 5초 후 자동 닫기
    if (type !== 'error') {
      toastTimerRef.current = setTimeout(() => setToast(null), 5000)
    }
  }, [])

  // ── 스토리지 설정 객체 (API 호출에 전달) ──────────────────────────────────
  const storageSettings = useMemo(() => ({
    storage_type:   settings.storage_type || APP_DEFAULTS.storage_type,
    gcs_path:       settings.gcs_path || '',
    fs_project:     settings.fs_project || '',
    fs_database:    settings.fs_database || '',
    fs_collection:  settings.fs_collection || '',
    user_id:        settings.user_id || '',
  }), [settings.storage_type, settings.gcs_path, settings.fs_project, settings.fs_database, settings.fs_collection, settings.user_id])

  // ── 뷰 목록 로드 ──────────────────────────────────────────────────────────
  const loadViews = useCallback(async () => {
    try {
      const list = await api.listViews(storageSettings)
      setSavedViews(list)
    } catch (e) {
      console.error('Failed to load views:', e)
      showToast('error', '뷰 목록 로드 실패: ' + e.message)
    }
  }, [storageSettings])

  useEffect(() => { loadViews() }, [loadViews])

  // ── 테마 / 폰트 크기 적용 ──────────────────────────────────────────────────
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', settings.theme || 'light')
    root.style.setProperty('--code-font-size', `${settings.font_size || 13}px`)
  }, [settings.theme, settings.font_size])

  const editorTheme = useMemo(() =>
    settings.theme === 'dark' ? oneDark : 'light'
  , [settings.theme])

  // 설정 라이브 프리뷰 — 저장 없이 화면만 즉시 갱신 (취소 시 SystemSettings가 원본 복원)
  const handlePreviewSettings = useCallback((partial) => {
    setSettings(prev => ({ ...prev, ...partial }))
  }, [])

  // ── 설정 저장 ─────────────────────────────────────────────────────────────
  const handleSaveSettings = useCallback(async (newSettings) => {
    const storageChanged =
      newSettings.storage_type !== settings.storage_type ||
      newSettings.gcs_path !== settings.gcs_path ||
      newSettings.fs_project !== settings.fs_project ||
      newSettings.fs_database !== settings.fs_database ||
      newSettings.fs_collection !== settings.fs_collection

    setSettings(newSettings)
    localStorage.setItem('svg_settings', JSON.stringify(newSettings))
    setSettingsPage(null)

    try {
      await api.saveUserSettings(newSettings)
      showToast('success', '설정이 저장되었습니다.')
    } catch (e) {
      showToast('error', `서버 저장 실패: ${e.message} (로컬에는 저장됨)`)
    }

    // 스토리지가 바뀌면 현재 뷰 선택 해제 + 즉시 새 스토리지에서 목록 재조회
    if (storageChanged) {
      setSelectedView(null)
      setSelectedViewOwner('')
      setSelectedViewIsMine(true)
      setViewName('')
      setDescription('')
      setPrompt('')
      setSemanticYaml(''); setSemanticYamlOrig(''); setSemanticYamlDirty(false)
      setSqlText('')
      setYamlContent(''); setYamlOriginal('')
      setDdl(''); setDdlOriginal('')
      setBqView(''); setBqViewOriginal('')
      setBqMv(''); setBqMvOriginal('')
      setBqMvAnalysis(null)
      setLookml(''); setLookmlOriginal('')
      setPubCtx(''); setPubCtxOriginal('')
      setValidation(null)
      setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setIsSaved(false)
    }
  }, [settings, showToast])

  // KBNL-70: 단일 필드 patch — Sandbox 헤더의 빠른 토글 등에서 사용.
  // 전체 settings 페이지 안 거치고 즉시 저장. localStorage 도 함께 갱신.
  const handlePatchSettings = useCallback(async (partial) => {
    let nextSettings = null
    setSettings(prev => {
      nextSettings = { ...prev, ...partial }
      try {
        localStorage.setItem('svg_settings', JSON.stringify(nextSettings))
      } catch { /* ignore quota */ }
      return nextSettings
    })
    if (!nextSettings) return
    try {
      await api.saveUserSettings(nextSettings)
    } catch (e) {
      showToast('error', `설정 저장 실패: ${e.message}`)
    }
  }, [showToast])

  // 공유 기본값 저장 (admin 전용) — 로컬 settings/localStorage 변경 안 함
  const handleSaveDefaultSettings = useCallback(async (newSettings) => {
    try {
      await api.saveDefaultSettings(newSettings)
      setSettingsPage(null)
      showToast('success', '공유 기본값이 저장되었습니다.')
    } catch (e) {
      showToast('error', `공유 기본값 저장 실패: ${e.message}`)
    }
  }, [showToast])

  // ── 새 뷰 ─────────────────────────────────────────────────────────────────
  const handleNewView = useCallback(() => {
    setViewName('')
    setDescription('')
    setPrompt('')
    setSemanticYaml(''); setSemanticYamlOrig(''); setSemanticYamlDirty(false)
    setSqlText('')
    setYamlContent('')
    setYamlOriginal('')
    setDdl(''); setDdlOriginal('')
    setBqView(''); setBqViewOriginal('')
    setBqMv(''); setBqMvOriginal('')
    setBqMvAnalysis(null)
    setLookml(''); setLookmlOriginal('')
    setPubCtx(''); setPubCtxOriginal('')
    setValidation(null)
    setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
    setIsSaved(false)
    setSelectedView(null)
    setSelectedViewOwner('')
    setSelectedViewIsMine(true)
    setSelectedViewTokenCount(null)
    setActiveTab('semantic')
  }, [])

  // ── 저장된 뷰 선택 ────────────────────────────────────────────────────────
  const handleSelectView = useCallback(async (name, ownerUserId = '') => {
    try {
      const data = await api.getView(name, storageSettings, ownerUserId)
      setSelectedView(name)
      setSelectedViewOwner(data.owner_user_id || data.user_id || ownerUserId || '')
      setSelectedViewIsMine(data.is_mine !== false)
      setSelectedViewTokenCount(data.token_count ?? null)
      setViewName(data.name || name)
      setDescription(data.description || '')
      setPrompt(data.prompt || data.requirements || '')
      // SQL 원본
      setSqlText(data.sql_text || '')
      // Semantic View YAML: 저장된 값이 있으면 사용, 없으면 로드 후 변환
      const savedSemantic = data.semantic_yaml || ''
      setSemanticYaml(savedSemantic); setSemanticYamlOrig(savedSemantic); setSemanticYamlDirty(false)
      // KBNL-75: SV 본체 = metric_view_yaml 우선, legacy 'yaml_string' fallback
      const metricYaml = data.metric_view_yaml || data.yaml_string || ''
      setYamlContent(metricYaml)
      setYamlOriginal(metricYaml)
      setDdl(data.ddl || ''); setDdlOriginal(data.ddl || '')
      setBqView(data.bq_view || ''); setBqViewOriginal(data.bq_view || '')
      setBqMv(data.bq_mv || ''); setBqMvOriginal(data.bq_mv || '')
      setBqMvAnalysis(data.bq_mv_analysis || null)
      setLookml(data.lookml || ''); setLookmlOriginal(data.lookml || '')
      setPubCtx(data.published_context || ''); setPubCtxOriginal(data.published_context || '')
      setValidation(null)
      setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setIsSaved(true)
      setActiveTab('semantic')
      // 저장된 semantic_yaml이 없으면 metric yaml에서 변환 시도
      if (!savedSemantic && metricYaml) {
        try {
          const conv = await api.convert({
            metric_view_yaml: metricYaml,
            project_id: settings.project_id,
            dataset_id: settings.dataset_id,
            view_name: data.name || name,
            description: data.description || '',
          })
          if (conv.semantic_yaml) {
            setSemanticYaml(conv.semantic_yaml)
            setSemanticYamlOrig(conv.semantic_yaml)
          }
        } catch { /* ignore — semantic yaml is optional */ }
      }
    } catch (e) {
      showToast('error', `뷰 로드 실패: ${e.message}`)
    }
  }, [showToast, storageSettings, settings.project_id, settings.dataset_id])

  // ── 타이머 시작/정지 헬퍼 ─────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    setElapsed(0)
    startTimeRef.current = Date.now()
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000)), 1000)
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  // ── 스피너 메시지 순환 ──────────────────────────────────────────────────────
  const SPINNER_MESSAGES = useMemo(() => [
    'BigQuery 테이블 메타데이터 수집 중...',
    '컬럼 타입 및 스키마 분석 중...',
    '테이블 간 관계 추론 중...',
    'Dataplex Glossary 용어 조회 중...',
    '비즈니스 용어와 컬럼 매핑 중...',
    '컬럼 역할(Dimension/Measure) 추론 중...',
    '통합 메타데이터 컨텍스트 구축 중...',
    'Gemini 프롬프트 구성 중...',
    'Gemini에게 YAML 생성 요청 중...',
    'Gemini가 Metric View 구조를 설계하고 있습니다...',
    'Dimension 및 Measure 정의를 생성하고 있습니다...',
    'JOIN 관계를 분석하고 있습니다...',
    'SQL 힌트를 최적화하고 있습니다...',
    '필터 조건을 구성하고 있습니다...',
    '생성된 YAML 검증 중...',
    'YAML 스키마 적합성 확인 중...',
    '필수 필드 누락 여부 검사 중...',
    'Dimension 이름 중복 검사 중...',
    'Measure 집계 함수 검증 중...',
    '검증 오류 자동 수정 시도 중...',
    'Gemini에게 수정 요청 중...',
    '수정된 YAML 재검증 중...',
    'BigQuery View SQL 생성 준비 중...',
    'Materialized View 호환성 분석 중...',
    '멀티 타겟 출력 변환 중...',
    '거의 완료되었습니다...',
    'Gemini가 열심히 작업하고 있습니다...',
    '최적의 Metric View를 만들고 있습니다...',
  ], [])

  const [spinnerIdx, setSpinnerIdx] = useState(0)
  const spinnerIntervalRef = useRef(null)

  useEffect(() => {
    if (isGenerating) {
      setSpinnerIdx(0)
      spinnerIntervalRef.current = setInterval(() => {
        setSpinnerIdx(prev => (prev + 1) % SPINNER_MESSAGES.length)
      }, 3000)
    } else {
      if (spinnerIntervalRef.current) {
        clearInterval(spinnerIntervalRef.current)
        spinnerIntervalRef.current = null
      }
    }
    return () => {
      if (spinnerIntervalRef.current) clearInterval(spinnerIntervalRef.current)
    }
  }, [isGenerating, SPINNER_MESSAGES.length])

  // ── SQL 변환 스피너 ──────────────────────────────────────────────────────
  const SQL_SPINNER_MESSAGES = useMemo(() => [
    'SQL 쿼리 구조를 분석하고 있습니다...',
    'FROM/JOIN 절에서 테이블을 추출하고 있습니다...',
    'WHERE 조건과 필터를 파싱하고 있습니다...',
    'Oracle 문법을 감지하고 있습니다 (NVL, DECODE, TO_CHAR...)',
    'CTE(WITH 절) 구조를 분석하고 있습니다...',
    'SUM/COUNT 등 집계 함수를 분류하고 있습니다...',
    'Dataplex Glossary 용어를 조회하고 있습니다...',
    'Gemini에게 Semantic View 변환을 요청하고 있습니다...',
    'Gemini가 SQL을 Semantic View YAML로 변환하고 있습니다...',
    'CASE WHEN 표현식을 dimension으로 매핑하고 있습니다...',
    '집계 컬럼을 measure로 분류하고 있습니다...',
    'Oracle → BigQuery 문법을 변환하고 있습니다...',
    'dimension_group 계층 구조를 설계하고 있습니다...',
    'source/join/filter 구조를 생성하고 있습니다...',
    '생성된 YAML을 검증하고 있습니다...',
    'YAML 구조 오류를 자동 수정하고 있습니다...',
    'Gemini가 열심히 변환하고 있습니다...',
    '거의 완료되었습니다...',
  ], [])

  const [sqlSpinnerIdx, setSqlSpinnerIdx] = useState(0)
  const sqlSpinnerIntervalRef = useRef(null)

  useEffect(() => {
    if (sqlConverting) {
      setSqlSpinnerIdx(0)
      sqlSpinnerIntervalRef.current = setInterval(() => {
        setSqlSpinnerIdx(prev => (prev + 1) % SQL_SPINNER_MESSAGES.length)
      }, 3000)
    } else {
      if (sqlSpinnerIntervalRef.current) {
        clearInterval(sqlSpinnerIntervalRef.current)
        sqlSpinnerIntervalRef.current = null
      }
    }
    return () => {
      if (sqlSpinnerIntervalRef.current) clearInterval(sqlSpinnerIntervalRef.current)
    }
  }, [sqlConverting, SQL_SPINNER_MESSAGES.length])

  // ── 생성 중지 ──────────────────────────────────────────────────────────────
  const handleAbortGenerate = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null }
    const sec = Math.floor((Date.now() - startTimeRef.current) / 1000)
    stopTimer()
    setIsGenerating(false)
    showToast('error', `생성 중지됨 (${sec}초 경과)`)
  }, [stopTimer, showToast])

  // ── 생성 ──────────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!viewName.trim()) { showToast('error', '뷰 이름을 입력해주세요.'); return }
    if (!settings.project_id) { showToast('error', '설정에서 GCP Project ID를 입력해주세요.'); return }
    const hasTables = settings.selected_tables && Object.values(settings.selected_tables).some(t => t.length > 0)
    if (!hasTables && !settings.table_ids?.length) { showToast('error', '설정에서 테이블을 선택해주세요.'); return }

    const controller = new AbortController()
    abortRef.current = controller

    setIsGenerating(true)
    startTimer()
    setSemanticYaml(''); setSemanticYamlOrig(''); setSemanticYamlDirty(false)
    setYamlContent('')
    setDdl(''); setBqView(''); setBqMv(''); setBqMvAnalysis(null); setLookml(''); setPubCtx('')
    setValidation(null)

    try {
      const result = await api.generate({
        ...settings,
        view_name: viewName,
        description,
        requirements: prompt,
      }, { signal: controller.signal })
      setSemanticYaml(result.semantic_yaml || ''); setSemanticYamlOrig(result.semantic_yaml || ''); setSemanticYamlDirty(false)
      // KBNL-75: 신규 metric_view_yaml 우선, legacy 'yaml_string' fallback
      const genYaml = result.metric_view_yaml || result.yaml_string || ''
      setYamlContent(genYaml)
      setYamlOriginal(genYaml)
      setDdl(result.ddl); setDdlOriginal(result.ddl)
      setBqView(result.bq_view); setBqViewOriginal(result.bq_view)
      setBqMv(result.bq_mv || ''); setBqMvOriginal(result.bq_mv || '')
      setBqMvAnalysis(result.bq_mv_analysis || null)
      setLookml(result.lookml); setLookmlOriginal(result.lookml)
      setPubCtx(result.published_context || ''); setPubCtxOriginal(result.published_context || '')
      setValidation(result.validation)
      setSemanticYamlDirty(false); setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setIsSaved(false)
      setActiveTab('semantic')
      const sec = Math.floor((Date.now() - startTimeRef.current) / 1000)
      showToast('success', `생성 완료 (${sec}초, 수정 ${result.fix_attempts}회)`)
    } catch (e) {
      if (e.name === 'AbortError') return  // 중지됨 — handleAbortGenerate에서 처리
      showToast('error', `생성 실패: ${e.message}`)
    } finally {
      stopTimer()
      setIsGenerating(false)
      abortRef.current = null
    }
  }, [viewName, description, prompt, settings, showToast, startTimer, stopTimer])

  // ── YAML 재변환 (공통 API 호출) ───────────────────────────────────────────
  const [convertingTab, setConvertingTab] = useState(null)  // null | 'all' | 'ddl' | 'bq' | 'bq_mv' | 'lookml'

  const callConvertApi = useCallback(() => {
    return api.convert({
      // KBNL-75: yaml_string → metric_view_yaml
      metric_view_yaml: yamlContent,
      project_id: settings.project_id,
      dataset_id: settings.dataset_id,
      view_name: viewName || 'metric_view',
      description,
      catalog: settings.catalog,
      schema_name: settings.schema_name,
    })
  }, [yamlContent, viewName, description, settings])

  // Semantic View YAML → 전체 재변환 (Metric View YAML + 모든 출력)
  // ── SQL → Semantic View 변환 ──────────────────────────────────────────────
  const handleConvertFromSql = useCallback(async () => {
    if (!sqlText.trim()) { showToast('warning', 'SQL을 입력하세요.'); return }
    if (!settings.project_id) { showToast('error', '설정에서 Project ID를 입력하세요.'); return }
    setSqlConverting(true)
    setSqlElapsed(0)
    sqlTimerRef.current = setInterval(() => setSqlElapsed(e => e + 1), 1000)
    try {
      const res = await api.sqlToSemantic({
        sql_text: sqlText,
        project_id: settings.project_id,
        location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
        model_name: settings.model_name || APP_DEFAULTS.model_name,
        glossary_id: settings.glossary_id || '',
        glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
        reference_views: [],
        user_requirements: prompt,
        storage_type: storageSettings?.storage_type || APP_DEFAULTS.storage_type,
        gcs_path: storageSettings?.gcs_path || '',
        fs_project: storageSettings?.fs_project || '',
        fs_database: storageSettings?.fs_database || '',
        fs_collection: storageSettings?.fs_collection || '',
        user_id: storageSettings?.user_id || '',
      })
      setSemanticYaml(res.semantic_yaml || '')
      setSemanticYamlOrig(res.semantic_yaml || '')
      setSemanticYamlDirty(false)
      setActiveTab('semantic')
      const fixMsg = res.fix_attempts > 0 ? ` (자동 수정 ${res.fix_attempts}회)` : ''
      showToast('success', `SQL → Semantic View 변환 완료${fixMsg}`)
    } catch (e) {
      showToast('error', `변환 실패: ${e.message}`)
    } finally {
      setSqlConverting(false)
      clearInterval(sqlTimerRef.current)
    }
  }, [sqlText, prompt, settings, storageSettings, showToast])

  useEffect(() => {
    return () => { if (sqlTimerRef.current) clearInterval(sqlTimerRef.current) }
  }, [])

  const handleConvertFromSemantic = useCallback(async () => {
    if (!semanticYaml) return
    setConvertingTab('all'); setIsConverting(true)
    try {
      const result = await api.convertSemantic({
        semantic_yaml: semanticYaml,
        project_id: settings.project_id,
        dataset_id: settings.dataset_id,
        view_name: viewName || 'metric_view',
        description,
        catalog: settings.catalog,
        schema_name: settings.schema_name,
      })
      // KBNL-75: 신규 metric_view_yaml 우선, legacy fallback
      const convertedYaml = result.metric_view_yaml || result.yaml_string || ''
      setYamlContent(convertedYaml); setYamlOriginal(convertedYaml)
      setDdl(result.ddl); setDdlOriginal(result.ddl)
      setBqView(result.bq_view); setBqViewOriginal(result.bq_view)
      setBqMv(result.bq_mv || ''); setBqMvOriginal(result.bq_mv || '')
      setBqMvAnalysis(result.bq_mv_analysis || null)
      setLookml(result.lookml); setLookmlOriginal(result.lookml)
      setPubCtx(result.published_context || ''); setPubCtxOriginal(result.published_context || '')
      setSemanticYamlDirty(false); setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setSemanticYamlOrig(semanticYaml)
      showToast('success', '전체 출력 변환 완료')
    } catch (e) {
      showToast('error', `변환 실패: ${e.message}`)
    } finally {
      setConvertingTab(null); setIsConverting(false)
    }
  }, [semanticYaml, viewName, description, settings, showToast])

  // Metric View YAML → 출력 재변환 (BQ View, BQ MV 등)
  const handleConvertAll = useCallback(async () => {
    if (!yamlContent) return
    setConvertingTab('all'); setIsConverting(true)
    try {
      const result = await callConvertApi()
      if (result.semantic_yaml) {
        setSemanticYaml(result.semantic_yaml); setSemanticYamlOrig(result.semantic_yaml); setSemanticYamlDirty(false)
      }
      setDdl(result.ddl); setDdlOriginal(result.ddl)
      setBqView(result.bq_view); setBqViewOriginal(result.bq_view)
      setBqMv(result.bq_mv || ''); setBqMvOriginal(result.bq_mv || '')
      setBqMvAnalysis(result.bq_mv_analysis || null)
      setLookml(result.lookml); setLookmlOriginal(result.lookml)
      setPubCtx(result.published_context || ''); setPubCtxOriginal(result.published_context || '')
      setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setYamlOriginal(yamlContent)
      showToast('success', '전체 출력 변환 완료')
    } catch (e) {
      showToast('error', `변환 실패: ${e.message}`)
    } finally {
      setConvertingTab(null); setIsConverting(false)
    }
  }, [yamlContent, callConvertApi, showToast])

  // 탭별 개별 재변환
  const handleConvertTab = useCallback(async (tab) => {
    if (!yamlContent) return
    setConvertingTab(tab); setIsConverting(true)
    try {
      const result = await callConvertApi()
      if (tab === 'bq')      { setBqView(result.bq_view); setBqViewOriginal(result.bq_view); setBqViewDirty(false) }
      if (tab === 'bq_mv')   { setBqMv(result.bq_mv || ''); setBqMvOriginal(result.bq_mv || ''); setBqMvAnalysis(result.bq_mv_analysis || null); setBqMvDirty(false) }
      if (tab === 'pub_ctx') { setPubCtx(result.published_context || ''); setPubCtxOriginal(result.published_context || ''); setPubCtxDirty(false) }
      showToast('success', '변환 완료')
    } catch (e) {
      showToast('error', `변환 실패: ${e.message}`)
    } finally {
      setConvertingTab(null); setIsConverting(false)
    }
  }, [yamlContent, callConvertApi, showToast])

  // ── 저장 ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!viewName.trim()) { showToast('error', '뷰 이름을 입력해주세요.'); return }
    if (!yamlContent && !semanticYaml) { showToast('error', '저장할 YAML이 없습니다.'); return }

    setIsSaving(true)
    try {
      const saveResult = await api.saveView({
        name: viewName,
        description,
        // KBNL-75: yaml_string → metric_view_yaml (alias 로 legacy 키도 수용되지만 신규 키로 명시)
        metric_view_yaml: yamlContent,
        semantic_yaml: semanticYaml,
        sql_text: sqlText,
        ddl, bq_view: bqView, bq_mv: bqMv, bq_mv_analysis: bqMvAnalysis || {}, lookml, published_context: pubCtx,
        project_id: settings.project_id,
        dataset_id: settings.dataset_id,
        table_ids: settings.table_ids,
        selected_tables: settings.selected_tables || {},
        prompt,
        settings,
      }, storageSettings)
      // 서버에서 description 주석이 반영된 YAML로 갱신
      // KBNL-75: 신규 metric_view_yaml 우선, legacy 'yaml_string' fallback
      const savedYaml = saveResult.metric_view_yaml || saveResult.yaml_string || ''
      if (savedYaml && savedYaml !== yamlContent) {
        setYamlContent(savedYaml)
      }
      setSelectedView(viewName)
      setSelectedViewOwner(saveResult?.owner_user_id || storageSettings?.user_id || '')
      setSelectedViewIsMine(true)
      setSelectedViewTokenCount(saveResult?.token_count ?? null)
      setIsSaved(true)
      setSemanticYamlDirty(false); setYamlDirty(false); setDdlDirty(false); setBqViewDirty(false); setBqMvDirty(false); setLookmlDirty(false); setPubCtxDirty(false)
      setSemanticYamlOrig(semanticYaml)
      setYamlOriginal(savedYaml || yamlContent)
      setDdlOriginal(ddl); setBqViewOriginal(bqView); setBqMvOriginal(bqMv); setLookmlOriginal(lookml); setPubCtxOriginal(pubCtx)
      await loadViews()
      showToast('success', `'${viewName}' 저장 완료`)
    } catch (e) {
      showToast('error', `저장 실패: ${e.message}`)
    } finally {
      setIsSaving(false)
    }
  }, [viewName, description, yamlContent, semanticYaml, ddl, bqView, bqMv, bqMvAnalysis, lookml, pubCtx, prompt, settings, storageSettings, loadViews, showToast])

  // ── 이름 변경 ─────────────────────────────────────────────────────────────
  const handleRename = useCallback(async (oldName, newName, oldOwner = '') => {
    try {
      await api.renameView(oldName, newName, storageSettings)
      if (selectedView === oldName && selectedViewOwner === oldOwner) {
        setSelectedView(newName)
        setViewName(newName)
      }
      await loadViews()
      showToast('success', `'${oldName}' → '${newName}' 이름 변경 완료`)
    } catch (e) {
      showToast('error', `이름 변경 실패: ${e.message}`)
    }
  }, [selectedView, selectedViewOwner, loadViews, showToast, storageSettings])

  // ── 복제 (Duplicate) ──────────────────────────────────────────────────────
  // Sidebar 의 본인 항목 hover 액션. 다이얼로그에서 새 이름 확정 → 본문 그대로
  // 새 doc 으로 저장. 백엔드는 이름 기준 overwrite 시맨틱이므로 신규 이름이면
  // 자연스럽게 신규 doc 으로 들어간다.
  const [duplicateDialog, setDuplicateDialog] = useState(null) // { sourceName, defaultName } | null
  const [duplicating, setDuplicating] = useState(false)

  const handleOpenDuplicateView = useCallback((src) => {
    if (!src) return
    const myViews = (savedViews || []).filter(v => v.is_mine !== false)
    setDuplicateDialog({
      sourceName: src.name,
      defaultName: suggestDuplicateName(src.name, myViews),
    })
  }, [savedViews])

  const handleConfirmDuplicateView = useCallback(async (newName) => {
    if (!duplicateDialog) return
    const sourceName = duplicateDialog.sourceName
    const finalName = (newName || '').trim()
    if (!finalName) { showToast('error', '이름을 입력하세요.'); return }
    const myViews = (savedViews || []).filter(v => v.is_mine !== false)
    if (myViews.some(v => v.name === finalName)) {
      showToast('error', `이미 '${finalName}' 이름의 Semantic View 가 존재합니다.`)
      return
    }
    setDuplicating(true)
    try {
      const full = await api.getView(sourceName, storageSettings)
      await api.saveView({ ...full, name: finalName }, storageSettings)
      showToast('success', `'${sourceName}' → '${finalName}' 복제 완료`)
      setDuplicateDialog(null)
      await loadViews()
    } catch (e) {
      showToast('error', `복제 실패: ${e.message}`)
    } finally {
      setDuplicating(false)
    }
  }, [duplicateDialog, savedViews, storageSettings, loadViews, showToast])

  // ── 삭제 ──────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    if (!selectedView) return
    // KBNL-141(리뷰): 타인 SV 는 삭제 차단 — 같은 이름의 내 SV 가 대신 지워지는 사고 방지.
    if (!selectedViewIsMine) {
      showToast('error', '다른 사용자의 Semantic View 는 삭제할 수 없습니다.')
      return
    }
    if (!window.confirm(`'${selectedView}' 뷰를 삭제할까요?`)) return
    try {
      await api.deleteView(selectedView, storageSettings)
      handleNewView()
      await loadViews()
      showToast('success', `'${selectedView}' 삭제 완료`)
    } catch (e) {
      showToast('error', `삭제 실패: ${e.message}`)
    }
  }, [selectedView, selectedViewIsMine, handleNewView, loadViews, showToast, storageSettings])

  // ── 편집 감지 ────────────────────────────────────────────────────────────
  const handleSemanticYamlChange = useCallback((val) => { setSemanticYaml(val); setSemanticYamlDirty(true) }, [])
  const handleYamlChange   = useCallback((val) => { setYamlContent(val); setYamlDirty(true) }, [])
  const handleDdlChange    = useCallback((val) => { setDdl(val);         setDdlDirty(true) }, [])
  const handleBqViewChange = useCallback((val) => { setBqView(val);      setBqViewDirty(true) }, [])
  const handleBqMvChange   = useCallback((val) => { setBqMv(val);        setBqMvDirty(true) }, [])
  const handleLookmlChange = useCallback((val) => { setLookml(val);      setLookmlDirty(true) }, [])
  const handlePubCtxChange = useCallback((val) => { setPubCtx(val);      setPubCtxDirty(true) }, [])

  // 어떤 탭이든 수정됐는지
  const anyDirty = semanticYamlDirty || yamlDirty || ddlDirty || bqViewDirty || bqMvDirty || lookmlDirty || pubCtxDirty
  // 콘텐츠가 있는데 아직 한 번도 저장 안 된 상태
  const isUnsaved = !isSaved && (!!semanticYaml || !!yamlContent || !!ddl || !!bqView || !!lookml)

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  if (authEnabled === null) return <div className="app" style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><div className="spinner"/></div>
  if (authEnabled && !authChecked) return <div className="app" style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh'}}><div className="spinner"/></div>
  if (authEnabled && !authUser) return <LoginPage onLogin={handleLogin} />

  return (
    <div className="app">
      {/* 헤더 */}
      <header className="header">
        <span className="header-logo">◆</span>
        <span className="header-title">DAVIS Semantic View Studio</span>
        <div className="mode-toggle">
          {/* KBNL-93 후속: Explore 메뉴는 env EXPLORE_MENU_ENABLED 가 true 일 때만 노출.
              기본 숨김 (D4 결정). URL hash '#explore' 직접 접근은 그대로 동작. */}
          {exploreMenuEnabled && (
            <button
              className={`mode-btn ${appMode === 'explore' ? 'active' : ''}`}
              onClick={() => switchMode('explore')}
            >Explore</button>
          )}
          {/* KBNL-99 §2: 통합 Workbench — 기본 진입점. SqlConverterWorkbench 가
              뒷받침. 기존 SQL Converter / SQL Workbench / Semantic View 는
              LEGACY_MENUS_ENABLED env 로 숨김 (URL hash 직접 접근은 보존). */}
          <button
            className={`mode-btn ${appMode === 'sql_wb' ? 'active' : ''}`}
            onClick={() => switchMode('sql_wb')}
          >Workbench</button>
          {legacyMenusEnabled && (
            <>
              <button
                className={`mode-btn ${appMode === 'sql' ? 'active' : ''}`}
                onClick={() => switchMode('sql')}
                title="(legacy) SQL Converter — 통합 Workbench 로 대체됨"
              >SQL Converter</button>
              <button
                className={`mode-btn ${appMode === 'metric' ? 'active' : ''}`}
                onClick={() => switchMode('metric')}
                title="(legacy) Semantic View — 통합 Workbench 로 대체됨"
              >Semantic View</button>
            </>
          )}
          <button
            className={`mode-btn ${appMode === 'semantic' ? 'active' : ''}`}
            onClick={() => switchMode('semantic')}
          >Composite View</button>
          {/* KBNL-57: VIRTUAL_SCHEMA_ENABLED 가 false 면 메뉴 탭 숨김 */}
          {virtualSchemaEnabled && (
            <button
              className={`mode-btn ${appMode === 'vschema' ? 'active' : ''}`}
              onClick={() => switchMode('vschema')}
            >Virtual Schema</button>
          )}
          {/* KBNL-93: Sandbox 는 우상단 작은 아이콘으로 이동 — 아래 .btn-icon 그룹 참조.
              누구나 사용 가능, 단지 큰 메뉴로 노출되지 않게만. */}
        </div>

        {authUser && (
          <span className="header-user">{authUser.last_name}{authUser.first_name}</span>
        )}
        {/* KBNL-93: Sandbox 작은 아이콘 (플라스크). active 강조는 기존 아이콘 패턴과 동일. */}
        <button
          className={`btn-icon ${appMode === 'sandbox' ? 'active' : ''}`}
          onClick={() => switchMode('sandbox')}
          onMouseEnter={() => import('./components/SandboxChat/SandboxChat')}
          onFocus={() => import('./components/SandboxChat/SandboxChat')}
          title="Sandbox"
        >{'\u{1F52C}\uFE0E'}</button>
        <button className="btn-icon" onClick={() => setShowProfile(true)} title="내 프로필" disabled={!authUser}>{'\u{1F464}\uFE0E'}</button>
        <button
          className={`btn-icon ${settingsPage === 'meta' ? 'active' : ''}`}
          onClick={() => setSettingsPage(p => p === 'meta' ? null : 'meta')}
          title="메타 설정"
        >🗂{'\uFE0E'}</button>
        <button
          className={`btn-icon ${settingsPage === 'system' ? 'active' : ''}`}
          onClick={() => setSettingsPage(p => p === 'system' ? null : 'system')}
          title="시스템 설정"
        >⚙</button>
        <button
          className={`btn-icon ${settingsPage === 'instructions' ? 'active' : ''}`}
          onClick={() => setSettingsPage(p => p === 'instructions' ? null : 'instructions')}
          title="인스트럭션 편집"
          disabled={!authUser}
        >📝</button>
      </header>

      {settingsPage === 'meta' ? (
        <MetaSettings
          settings={settings}
          isAdmin={!!authUser?.is_svs_admin}
          virtualSchemaEnabled={virtualSchemaEnabled}
          onSaveUser={handleSaveSettings}
          onSaveDefault={handleSaveDefaultSettings}
          onClose={() => setSettingsPage(null)}
          showToast={showToast}
        />
      ) : settingsPage === 'system' ? (
        <SystemSettings
          settings={settings}
          isAdmin={!!authUser?.is_svs_admin}
          onSaveUser={handleSaveSettings}
          onSaveDefault={handleSaveDefaultSettings}
          onClose={() => setSettingsPage(null)}
          onPreview={handlePreviewSettings}
          showToast={showToast}
        />
      ) : settingsPage === 'instructions' ? (
        <InstructionEditor
          settings={settings}
          isAdmin={!!authUser?.is_svs_admin}
          onClose={() => setSettingsPage(null)}
          showToast={showToast}
          editorTheme={editorTheme}
        />
      ) : appMode === 'semantic' ? (
        <div className="layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <CompositeViewer showToast={showToast} storageSettings={storageSettings} editorTheme={editorTheme} settings={settings} />
        </div>
      ) : appMode === 'sql' ? (
        <div className="layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <SqlConverter
            showToast={showToast}
            storageSettings={storageSettings}
            editorTheme={editorTheme}
            settings={settings}
            onNavigateToView={(name, ownerUserId) => { setAppMode('metric'); handleSelectView(name, ownerUserId || '') }}
          />
        </div>
      ) : appMode === 'sql_wb' ? (
        // KBNL-105 Stage B(B1): Workbench 는 아래에서 상시 마운트(display 토글)된다.
        //   탭 전환 시 언마운트되지 않아 진행 중 변환·스피너·결과가 유지되므로
        //   여기 삼항 분기에서는 렌더하지 않는다(이중 마운트 방지).
        null
      ) : appMode === 'explore' ? (
        <div className="layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Explore
            showToast={showToast}
            storageSettings={storageSettings}
            myUserId={authUser?.user_id || settings.user_id || ''}
            myUser={authUser}
            isAdmin={!!authUser?.is_svs_admin}
            editorTheme={editorTheme}
          />
        </div>
      ) : appMode === 'vschema' ? (
        <VirtualSchemaViewer
          showToast={showToast}
          settings={settings}
          setSettings={setSettings}
          authUser={authUser}
          editorTheme={editorTheme}
        />
      ) : appMode === 'sandbox' ? (
        <Suspense fallback={<SandboxFallback />}>
          <SandboxChat
            showToast={showToast}
            settings={settings}
            virtualSchemaEnabled={virtualSchemaEnabled}
            onOpenMetaSettings={() => setSettingsPage('meta')}
            onPatchSettings={handlePatchSettings}
          />
        </Suspense>
      ) :(
      <div className="layout">
        {/* 사이드바 */}
        <Sidebar
          views={savedViews}
          selected={selectedView ? `${selectedViewOwner}::${selectedView}` : ''}
          onSelect={handleSelectView}
          onNew={handleNewView}
          onRename={handleRename}
          onDuplicate={handleOpenDuplicateView}
        />

        {/* 메인 */}
        <main className="main">
          <GenerateBar
            viewName={viewName}
            onViewNameChange={setViewName}
            description={description}
            onDescriptionChange={setDescription}
            prompt={prompt}
            onPromptChange={setPrompt}
            onGenerate={handleGenerate}
            onSave={handleSave}
            onDelete={handleDelete}
            isGenerating={isGenerating}
            isSaving={isSaving}
            isConverting={isConverting}
            hasYaml={!!yamlContent || !!semanticYaml}
            hasSelected={!!selectedView}
            canDelete={selectedViewIsMine}
          />

          {/* 탭 헤더 */}
          <div className="tab-bar">
            {/* Semantic View YAML 기본 탭 */}
            <button
              className={`tab-btn tab-primary ${activeTab === 'semantic' ? 'active' : ''} ${semanticYamlDirty ? 'modified' : ''}`}
              onClick={() => setActiveTab('semantic')}
            >
              ◆ Semantic View YAML
              {semanticYamlDirty && <span className="modified-dot" title="수정됨" />}
            </button>

            <span className="tab-separator" />

            {/* 출력 탭 (Metric View YAML 포함) */}
            {[
              { key: 'yaml',   label: 'Metric View YAML',   dirty: yamlDirty },
              { key: 'bq',     label: 'BigQuery View',       dirty: bqViewDirty },
              { key: 'bq_mv',  label: bqMvAnalysis?.compatible === false ? 'BQ MV (불가)' : 'BQ MV', dirty: bqMvDirty },
              { key: 'lookml', label: 'LookML',             dirty: lookmlDirty },
              { key: 'pub_ctx',label: 'BQ Agent Context',     dirty: pubCtxDirty },
              { key: 'ddl',    label: 'Databricks DDL',     dirty: ddlDirty },
            ].filter(t => !DISABLED_TABS.has(t.key)).map(t => (
              <button
                key={t.key}
                className={`tab-btn ${activeTab === t.key ? 'active' : ''} ${t.dirty ? 'modified' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
                {t.dirty && <span className="modified-dot" title="수정됨" />}
              </button>
            ))}

            {/* 미저장 / 검증 배지 */}
            {isUnsaved && (
              <span className="unsaved-badge" title="아직 저장되지 않았습니다">미저장</span>
            )}
            {anyDirty && isSaved && (
              <span className="unsaved-badge dirty" title="저장 후 수정됨">수정됨</span>
            )}
            {validation && (
              <span className={`validation-badge ${validation.valid ? 'ok' : 'warn'}`}>
                {validation.valid
                  ? '✓ Valid'
                  : `⚠ ${validation.errors.length} error${validation.errors.length > 1 ? 's' : ''}`}
              </span>
            )}
          </div>

          {/* 에디터 / 출력 */}
          <div className="editor-area">
            {activeTab === 'semantic' && (
              <div className="yaml-main-panel" style={{ display: 'flex', flexDirection: 'column' }}>
                {semanticYamlDirty && semanticYaml && (
                  <div className="reconvert-banner">
                    <span>Semantic View YAML이 수정되었습니다. 출력 탭에 반영하려면 재변환하세요.</span>
                    <button
                      className="btn-reconvert"
                      onClick={handleConvertFromSemantic}
                      disabled={convertingTab === 'all'}
                    >
                      {convertingTab === 'all' ? '변환 중...' : '⟳ 전체 재변환'}
                    </button>
                  </div>
                )}
                {/* 도구 바 */}
                <div style={{ padding: '4px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-secondary)', flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    SQL → Semantic 변환은 상단 <strong>SQL Converter</strong> 탭에서 이용하세요.
                  </span>
                  <span style={{ flex: 1 }} />
                  {/* KBNL-69: 본문 헤더 토큰 배지 — 마지막 저장 기준 */}
                  {selectedView && (
                    <TokenBadge
                      count={selectedViewTokenCount}
                      variant="strong"
                      title="이 Semantic View 의 저장된 토큰 수 — 편집 후 저장 시 갱신"
                    />
                  )}
                  {/* KBNL-74: 현재 선택된 SV 와 유사한 SV 찾기 (모달, single-select 네비게이션) */}
                  {selectedView && (
                    <button
                      className="btn-xs"
                      onClick={() => setRecommendOpen(true)}
                      title="이 Semantic View 와 유사한 다른 SV 찾기 (의미 + 테이블 기반)"
                    >🔍 유사 SV 찾기</button>
                  )}
                  {semanticYaml && <CopyButton value={semanticYaml} />}
                  {/* 다운로드 + GCS Export (KBNL-27) */}
                  {semanticYaml && (
                    <>
                      <button
                        className="btn-xs"
                        onClick={() => downloadText(ensureExtension(viewName || 'semantic_view', 'yaml'), semanticYaml, 'text/yaml')}
                        title="현재 Semantic View YAML 을 로컬 파일로 다운로드"
                      >⤓ 다운로드</button>
                      <button
                        className="btn-xs"
                        onClick={() => setShowSemanticExport(true)}
                        title="GCS 의 특정 위치로 export"
                      >☁ GCS Export</button>
                    </>
                  )}
                </div>

                {/* Semantic YAML 에디터 */}
                <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <YamlEditor
                      value={semanticYaml}
                      originalValue={semanticYamlOrig}
                      onChange={handleSemanticYamlChange}
                      placeholder="뷰 이름과 설명을 입력하고 [생성]을 클릭하세요."
                      editorTheme={editorTheme}
                    />
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'yaml' && (
              <OutputTabs lang="yaml" value={yamlContent} label="Metric View YAML" onChange={handleYamlChange} dirty={yamlDirty}
                originalValue={yamlOriginal} yamlDirty={semanticYamlDirty} onConvert={handleConvertFromSemantic} isConverting={convertingTab === 'all'} editorTheme={editorTheme} />
            )}
            {activeTab === 'bq' && (
              <OutputTabs lang="sql" value={bqView} label="BigQuery View SQL" onChange={handleBqViewChange} dirty={bqViewDirty}
                originalValue={bqViewOriginal} yamlDirty={semanticYamlDirty} onConvert={handleConvertFromSemantic} isConverting={convertingTab === 'bq' || convertingTab === 'all'} editorTheme={editorTheme} />
            )}
            {activeTab === 'bq_mv' && (
              <div className="bq-mv-panel">
                {bqMvAnalysis && (
                  <div className={`mv-analysis ${bqMvAnalysis.compatible ? 'mv-ok' : 'mv-warn'}`}>
                    <div className="mv-analysis-summary">
                      {bqMvAnalysis.compatible ? '✓ ' : '⚠ '}
                      {bqMvAnalysis.summary}
                    </div>
                    {bqMvAnalysis.constraints?.length > 0 && (
                      <ul className="mv-constraints">
                        {bqMvAnalysis.constraints.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <OutputTabs
                  lang="sql"
                  value={bqMv}
                  onChange={handleBqMvChange}
                  dirty={bqMvDirty}
                  originalValue={bqMvOriginal}
                  yamlDirty={semanticYamlDirty}
                  onConvert={handleConvertFromSemantic}
                  isConverting={convertingTab === 'bq_mv' || convertingTab === 'all'}
                  editorTheme={editorTheme}
                  label={bqMvAnalysis?.compatible === false
                    ? "BigQuery VIEW (Materialized View 불가 → fallback)"
                    : "BigQuery Materialized View SQL"}
                />
              </div>
            )}
            {activeTab === 'pub_ctx' && (
              <OutputTabs lang="json" value={pubCtx} label="BQ Agent Context JSON" onChange={handlePubCtxChange} dirty={pubCtxDirty}
                originalValue={pubCtxOriginal} yamlDirty={semanticYamlDirty} onConvert={handleConvertFromSemantic} isConverting={convertingTab === 'pub_ctx' || convertingTab === 'all'} editorTheme={editorTheme} />
            )}
          </div>

          {/* 검증 오류 상세 */}
          {validation && !validation.valid && activeTab === 'semantic' && (
            <div className="validation-panel">
              {validation.errors.map((e, i) => (
                <div key={i} className="validation-error">⚠ {e}</div>
              ))}
              {validation.warnings?.map((w, i) => (
                <div key={i} className="validation-warning">ℹ {w}</div>
              ))}
            </div>
          )}
        </main>
      </div>

      )}

      {/* KBNL-105 Stage B(B1): Workbench 상시 마운트 — 탭 전환 시 언마운트 방지로
          진행 중 변환/스피너/결과를 유지. 비활성 탭에선 display:none 으로 숨김
          (컴포넌트 인스턴스 + 진행 중 fetch + Stage A 캐시가 그대로 보존됨).
          KBNL-117: 설정 페이지(settingsPage) 열림 중엔 숨김 — 동시 표시 방지. */}
      <div className="layout" style={{ display: (appMode === 'sql_wb' && !settingsPage) ? 'flex' : 'none', flex: 1, overflow: 'hidden' }}>
        <SqlConverterWorkbench
          showToast={showToast}
          storageSettings={storageSettings}
          editorTheme={editorTheme}
          settings={settings}
          onNavigateToView={(name, ownerUserId) => { setAppMode('metric'); handleSelectView(name, ownerUserId || '') }}
        />
      </div>

      {/* 프로필 모달 */}
      {showProfile && (
        <ProfileModal
          user={authUser}
          authEnabled={authEnabled}
          onLogout={() => { setShowProfile(false); handleLogout() }}
          onClose={() => setShowProfile(false)}
        />
      )}

      {/* Semantic View 복제 다이얼로그 (KBNL-65) */}
      <DuplicateDialog
        open={!!duplicateDialog}
        defaultName={duplicateDialog?.defaultName || ''}
        busy={duplicating}
        title="Semantic View 복제"
        hint="같은 본문으로 새 Semantic View 가 내 워크스페이스에 만들어집니다."
        onConfirm={handleConfirmDuplicateView}
        onCancel={() => { if (!duplicating) setDuplicateDialog(null) }}
      />

      {/* KBNL-74: SV 추천 모달 — 선택된 SV 와 유사한 다른 SV 찾기 (single-select 네비)
          mode='seed_sv' → 모달이 backend /sv/extracted-keywords 호출해서
          vector 채널이 실제 사용하는 텍스트(desc + dim/measure 자연어) 를
          검색바에 prefill. 사용자에게 보이는 텍스트와 backend 가 임베딩에 쓰는
          텍스트가 일치 → 검색 결과 일관성 + 사용자 편집 가능.

          KBNL-74 후속: seedSvOverrideYaml/Description 으로 현재 에디터 상태를
          backend 에 전달 → 저장 안 한 변경도 즉시 검색에 반영 (옵션 A). */}
      <SvRecommendDialog
        open={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        mode="seed_sv"
        seedSvName={selectedView || ''}
        seedSvOverrideYaml={semanticYaml || ''}
        seedSvOverrideDescription={description || ''}
        storageSettings={storageSettings}
        showToast={showToast}
        editorTheme={editorTheme}
        onSelect={(c) => {
          // KBNL-141(리뷰): owner 전달 — 같은 이름 다른 owner 후보를 정확히 로드.
          if (c?.name) handleSelectView(c.name, c.owner_user_id || c.user_id || '')
        }}
        title={`'${selectedView || ''}' 와(과) 유사한 Semantic View`}
      />

      {/* GCS Export 다이얼로그 (Semantic View 메인 — KBNL-27) */}
      <GcsExportDialog
        open={showSemanticExport}
        onClose={() => setShowSemanticExport(false)}
        defaultPath={settings.gcs_path || 'gs://'}
        defaultFilename={ensureExtension(viewName || 'semantic_view', 'yaml')}
        defaultExt="yaml"
        content={semanticYaml || ''}
        contentType="text/yaml"
        showToast={showToast}
        title="Semantic View YAML 을 GCS 로 Export"
      />

      {/* 토스트 */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <span className="toast-msg">{toast.msg}</span>
          <button className="toast-close" onClick={() => setToast(null)}>✕</button>
        </div>
      )}

      {/* 생성 로딩 오버레이 */}
      {isGenerating && (
        <div className="overlay">
          <div className="spinner-box">
            <div className="spinner" />
            <div className="spinner-text">{SPINNER_MESSAGES[spinnerIdx]}</div>
            <div className="spinner-elapsed">{formatElapsed(elapsed)}</div>
            <button className="btn-abort" onClick={handleAbortGenerate}>
              ■ 중지
            </button>
          </div>
        </div>
      )}

      {/* SQL 변환 로딩 오버레이 */}
      {sqlConverting && (
        <div className="overlay">
          <div className="spinner-box">
            <div className="spinner" />
            <div className="spinner-text">{SQL_SPINNER_MESSAGES[sqlSpinnerIdx]}</div>
            <div className="spinner-elapsed">{formatElapsed(sqlElapsed)}</div>
          </div>
        </div>
      )}
    </div>
  )
}
